import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { SeedCredentialOutputConfig } from '@chatsdet/types';
import { API_URL } from '../config';
import { toApiError } from '../lib/api-error';

export type CredentialOutputConfig = SeedCredentialOutputConfig;

export type SeedDataOutput = {
  dataKey: string;
  name: string;
  description?: string;
  displayLabel: string;
  propertyId?: string;
  attributes?: Record<string, unknown>;
  credentialOutput?: CredentialOutputConfig;
};

/**
 * The one shape stored under `['workflow', <id>, 'seed-outputs']`.
 *
 * Exported and shared on purpose: `useSeedNodeOutputs` and
 * `useCallFlowNodeOutputs` write that same key from the same endpoint, and
 * React Query keeps ONE entry per key — so whichever hook services a refetch
 * decides what every other observer reads. They previously stored three
 * different wrappers here, and an observer expecting `{ id, outputs }` that
 * found `{ ok, outputs }` silently lost its data. Keep this the single
 * declaration so a future *structural* divergence (a differently-shaped
 * object entirely) is a type error, not a mystery — but note that optional
 * fields like `workflowName` are NOT enforced this way: the type allows any
 * writer to omit them, so each writer still has to deliberately supply every
 * field a reader may need, or a reader relying on it will silently see the
 * field's absent/default value instead of a type error.
 *
 * The cached object is a SUPERSET of this type — the endpoint also returns
 * `inputs`, which `CallFlowNodeConfig` projects under its own key. What's
 * declared here tracks the wire payload's stable fields, not what any
 * particular reader consumes (`ok` has no reader either); don't take it as an
 * exhaustive description of what's actually cached.
 *
 * One asymmetry survives deliberately, and it is NOT symmetric — read both
 * halves before reasoning about a failure here.
 *
 * This hook THROWS on a non-ok response, surfacing the failure rather than
 * swallowing it. A failure it services leaves `data` undefined, so once the
 * error settles the FlowDesigner hooks simply have no map entry — which reads
 * the same as "this flow declares no outputs", not as a loading state
 * (`isLoading` is false by then). Their underlying query results carry
 * `isError`, but neither hook surfaces that to its consumers.
 *
 * The reverse is worse. Those hooks swallow a non-ok response into
 * `{ ok: false, outputs: [] }`, which query-core stores as SUCCESSFUL `data`
 * with a fresh timestamp — so a transient 5xx they service makes this hook's
 * readers render "no outputs" for the full 60s staleTime. `SeedDataCatalog`'s
 * `outputsData?.outputs ?? summaryOutputs` does not save you: `[]` is not
 * nullish. The shape is always valid; the emptiness is not always real.
 * Pre-existing and out of scope here — but don't mistake it for safe.
 */
export type SeedOutputsResponse = {
  ok: boolean;
  outputs: SeedDataOutput[];
  workflowName?: string;
};

type UseSeedWorkflowOutputsOptions = {
  enabled?: boolean;
  /**
   * Seed the shared cache entry from a list payload the caller already has.
   *
   * `outputs` and `workflowName` are required TOGETHER, not independently
   * optional: this entry is read by every other observer of the key, and one
   * that omitted `workflowName` is exactly what made `transitiveSeedOutputs`
   * fall back to `''` and drop its namespaced `$var.<flow>.<key>` entries.
   * Binding them makes the next omission a compile error.
   */
  initialData?: { outputs: SeedDataOutput[]; workflowName: string };
};

export function useSeedWorkflowOutputs(
  workflowId: string | undefined,
  options?: UseSeedWorkflowOutputsOptions
) {
  const enabled = (options?.enabled ?? true) && !!workflowId;

  return useQuery<SeedOutputsResponse>({
    queryKey: ['workflow', workflowId, 'seed-outputs'],
    queryFn: async () => {
      if (!workflowId) throw new Error('No workflow ID');
      const res = await fetch(`${API_URL}/workflows/${workflowId}/seed-outputs`, {
        credentials: 'include',
      });
      if (!res.ok) throw await toApiError(res, 'Failed to fetch seed outputs');
      return res.json();
    },
    enabled,
    initialData: options?.initialData
      ? {
          ok: true,
          outputs: options.initialData.outputs,
          workflowName: options.initialData.workflowName,
        }
      : undefined,
    // Stamp the seeded entry STALE. Without this, query-core dates initialData
    // `Date.now()`, so a projection built from a list payload looks as fresh as
    // a real fetch and the other observers of this key trust it for their full
    // 60s staleTime. Zero still renders the seeded data immediately — status is
    // 'success' regardless of the timestamp — but marks it stale, so EVERY
    // observer refetches on mount, this hook's own included. The contract is
    // "seeding is a head start", not "every future field must be hand-mirrored
    // here forever".
    // Unconditional: query-core only reads this when initialData is present.
    initialDataUpdatedAt: 0,
  });
}

export function useUpdateSeedOutputs(workflowId: string | undefined) {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (seedOutputsJson: SeedDataOutput[]) => {
      if (!workflowId) throw new Error('No workflow ID');
      const res = await fetch(`${API_URL}/workflows/${workflowId}`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ seedOutputsJson }),
      });
      if (!res.ok) throw await toApiError(res, 'Failed to update seed outputs');
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['workflow', workflowId, 'seed-outputs'] });
      qc.invalidateQueries({ queryKey: ['workflow-detail', workflowId] });
      qc.invalidateQueries({ queryKey: ['workflows'] });
    },
  });
}
